import { Injectable } from '@angular/core';
import { API } from '../view-models/Constants';
import { map, Observable, tap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { TokenResponse } from '../view-models/auth.model';
import { AnswerPayload, AnswerResponse, AssessmentsResponse, ResultResponse, StartAssessmentResponse } from '../view-models/lms.models';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  public BaseURL = API.prodBaseURL;
  public apiKey = "msnd19829ASDn8786SS"
  private trainingBaseUrl = '/v1/training';
  
  constructor(public http: HttpClient, private cookie: CookieService) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.cookie.get('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
  }

  httpGetHelp(apiURL: string): Observable<any> {
    return this.http.get<any>(this.BaseURL + apiURL, {
      headers: this.getAuthHeaders()
    });
  }

  httpPostHelp(apiURL: string, data: any): Observable<any> {
    return this.http.post<any>(this.BaseURL + apiURL, data, {
      headers: this.getAuthHeaders()
    });
  }

  refreshAccessToken(refreshToken: string): Observable<string> {
    return this.http
      .post<any>(this.BaseURL+'/auth/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      })
      .pipe(map(response => response.access_token));
  }

  getPitch() {
    const url = "/pitch/life/LIC/Product1?age=30&dependants=2&city=NewYork&diseases=none&session_id=12345";
    return this.httpGetHelp(url);
  }

  getProducts(category: string) {
    let api = "/products/" + category + "/";
    return this.httpGetHelp(api);
  }

  getSalesPitchForHealth(payload: any, company: string, product: string) {
    const url = "/pitch/v2/health/" + company + "/" + product
    return this.httpPostHelp(url, payload);
  }

  getSalesPitchForLife(payload: any, company: string, product: string) {
    const url = "/pitch/v2/life/" + company + "/" + product
    return this.httpPostHelp(url, payload);
  }

  getProductResponse(payload: any, company: string, product: string, category: string) {
    const url = "/v1/underwriting-query/" + category + "/" + company + "/" + product;
    return this.httpPostHelp(url, payload);
  }

  getSalesPitchForHealthWithProfile(customer_profile: any, company: string, product: string) {
    const url = "/pitch/v2/health/" + company + "/" + product + "/" + "?customer_profile=" + customer_profile
    return this.httpGetHelp(url);
  }

  getSalesPitchForLifeWithProfie(customer_profile: any, company: string, product: string) {
    const url = "/pitch/v2/life/" + company + "/" + product + "?customer_profile=" + customer_profile
    return this.httpGetHelp(url);
  }

  getComparisonSummaryWithProfile(customer_profile: any, company: string, product: string, category: string
    , product2: string, company2: string
  ) {
    const url = "/compare/" + category + "/" + company + "/" + product + "?customer_profile=" + customer_profile
      + "&product2=" + product2 + "&company2=" + company2;
    return this.httpGetHelp(url);
  }

  getComparisonSummaryWithFormDetails(payload: any, company: string, product: string, category: string,
    product2: string, company2: string) {
      const url = "/compare/" + category + "/" + company + "/" + product + "?&product2=" + product2 + "&company2=" + company2;
      return this.httpPostHelp(url, payload);
  }

  getComparisonSummaryWithProfileV2(payload: any, company: string, product: string, category: string, sub_category: string){
    const url = "/compare/v2/"+ category + "/" + sub_category + "/" + company + "/" + product;
    return this.httpPostHelp(url,payload);
  }

  addFeedback(payload: any){
    const url = "/feedback";
    return this.httpPostHelp(url,payload);
  }

  getProductRecommendation(module: String, customer_profile: String){
    const url = "/reco/"+module+"?customer_profile=" + customer_profile;
    return this.httpGetHelp(url);
  }

  getCompanyLogo(company:String){
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.apiKey}`
    });
    const url = "/logo/"+company;
    return this.http.get(this.BaseURL+url, { responseType: 'blob', headers });
  }

  getTranscription(data: FormData){
    const url = "/audio/transcribe/";
    return this.httpPostHelp(url, data);
  }

  login(data:FormData){
    const url = "/auth/token"
    return this.httpPostHelp(url,data);
  }

  changePassword(payload: any){
    const url = "/users/change-password";
    return this.httpPostHelp(url, payload);
  }

  getUserProcess(){
    const url = '/users/me'
    return this.httpGetHelp(url);
  }

  /**
   * Request an OTP code via email.
   */
  requestOtp(email: string, name?: string): Observable<any> {
    // Appended BaseURL to target the correct environment
    return this.http.post(`${this.BaseURL}${this.trainingBaseUrl}/auth/request-otp`, { email, name });
  }

  /**
   * Verify the OTP code and retrieve the token pair.
   * Automatically stores the access_token and refresh_token in ngx-cookie-service.
   */
  verifyOtp(email: string, code: string): Observable<TokenResponse> {
    return this.http.post<TokenResponse>(`${this.BaseURL}${this.trainingBaseUrl}/auth/verify-otp`, { email, code }).pipe(
      tap((response: TokenResponse) => {
        if (response && response.access_token) {
          this.cookie.set('access_token', response.access_token, { path: '/' });
          this.cookie.set('refresh_token', response.refresh_token, { path: '/' });
        }
      })
    );
  }

  /**
   * Validate session and get user info.
   */
  checkMe(): Observable<any> {
    // Utilized existing httpGetHelp so it automatically attaches headers
    return this.httpGetHelp(`${this.trainingBaseUrl}/me`);
  }

  startAssessment(assessmentKey: string): Observable<StartAssessmentResponse> {
    return this.http.post<StartAssessmentResponse>(`${this.BaseURL}${this.trainingBaseUrl}/assessments/${assessmentKey}/start`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  saveAnswer(attemptId: number, payload: AnswerPayload): Observable<AnswerResponse> {
    return this.http.post<AnswerResponse>(`${this.BaseURL}${this.trainingBaseUrl}/attempts/${attemptId}/answer`, payload, {
      headers: this.getAuthHeaders()
    });
  }

  submitAssessment(attemptId: number): Observable<any> {
    return this.http.post<any>(`${this.BaseURL}${this.trainingBaseUrl}/attempts/${attemptId}/submit`, {}, {
      headers: this.getAuthHeaders()
    });
  }

  getAssessments(): Observable<AssessmentsResponse> {
    return this.http.get<AssessmentsResponse>(`${this.BaseURL}${this.trainingBaseUrl}/assessments`, {
      headers: this.getAuthHeaders()
    });
  }

  getAssessmentResult(attemptId: number): Observable<ResultResponse> {
    return this.http.get<ResultResponse>(`${this.BaseURL}${this.trainingBaseUrl}/attempts/${attemptId}/result`, {
      headers: this.getAuthHeaders()
    });
  }

  getTrainingChatResponse(payload: any) {
    // Hardcoded to Process / Training as requested
    const url = `${this.trainingBaseUrl}/chat/underwriting/Process/Training`;
    return this.http.post<any>(this.BaseURL + url, payload, {
      headers: this.getAuthHeaders()
    });
  }

  getTranscriptionForTraining(data: FormData) {
    // Uses this.trainingBaseUrl ('/v1/training') to construct the new path
    const url = `${this.trainingBaseUrl}/audio/transcribe`;
    return this.httpPostHelp(url, data);
  }

  getTrainingFeedback(): Observable<any> {
    return this.http.get<any>(`${this.BaseURL}${this.trainingBaseUrl}/feedback`, {
      headers: this.getAuthHeaders()
    });
  }

  submitTrainingFeedback(payload: any): Observable<any> {
    return this.http.post<any>(`${this.BaseURL}${this.trainingBaseUrl}/feedback`, payload, {
      headers: this.getAuthHeaders()
    });
  }
}